import styled from '@emotion/styled';
import {
    HeadingText30,
    HeadingText20,
    BodyTextMedium,
    pxToRem,
} from '@modernhealth/vital-components';
import { colors, mediaQuery } from '@modernhealth/vital-foundation';
import moment, { Moment } from 'moment';
import React, { useMemo } from 'react';
import checkIcon from 'src/public/img/check_mark.svg';

interface DailyPauseStreaksCalendarProps {
    now?: Moment;
    completed: string[];
}

const weekLength = 7;
const weekDays: string[] = moment.weekdaysMin().map(day => day.slice(0, 1));
const firstDay = weekDays.shift();
weekDays.push(firstDay ? firstDay : '');

export const DailyPauseStreaksCalendar: React.FC<DailyPauseStreaksCalendarProps> = ({
    now = moment(),
    completed,
}) => {
    const startDay = now.clone().startOf('month').startOf('week');
    const day =
        startDay.date() === 1 ? startDay.clone().subtract(weekLength, 'day') : startDay.clone();
    const currentMonth = now.clone().format('MMMM');

    const calendar: Array<Moment[]> = useMemo(
        () =>
            Array(6)
                .fill(0)
                .map(() =>
                    Array(weekLength)
                        .fill(0)
                        .map(() => day.add(1, 'day').clone()),
                ),
        [day],
    );

    return (
        <CalendarContainer>
            <HeadingText30 css={{ textTransform: 'capitalize' }} centered marginBottom={20}>
                {currentMonth}
            </HeadingText30>
            <WeekDays>
                {weekDays.map((day, index) => (
                    <WeekDay as="span" key={`day-name-${index}`} fontColor="gray55">
                        {day}
                    </WeekDay>
                ))}
            </WeekDays>
            {calendar.map((week, weekIndex) => (
                <WeekRow key={`week-${weekIndex}`}>
                    {week.map((day, dayIndex) => (
                        <Day
                            as="span"
                            key={`day-number-${dayIndex}`}
                            grayOut={day.month() !== now.month()}
                            today={moment(day).isSame(now, 'day')}
                            completed={
                                completed &&
                                !!completed.find(date => moment(date).isSame(day, 'day'))
                            }
                        >
                            {day.format('D').toString()}
                        </Day>
                    ))}
                </WeekRow>
            ))}
        </CalendarContainer>
    );
};

const CalendarContainer = styled.div({
    width: '100%',
    maxWidth: pxToRem(432),
    padding: pxToRem(12, 16, 16),
    border: `2px solid ${colors.gray05}`,
    borderRadius: pxToRem(6),

    [mediaQuery.minWidth('sm')]: {
        padding: pxToRem(20, 38, 38),
    },
});

const WeekDays = styled.div({
    marginBottom: pxToRem(24),
    display: 'flex',
    justifyContent: 'space-between',
});

const WeekDay = styled(BodyTextMedium)({ textAlign: 'center', width: pxToRem(36) });

const WeekRow = styled.div`
    display: flex;
    justify-content: space-between;

    &:not(:last-child) {
        margin-bottom: ${pxToRem(18)};
    }
`;

const Day = styled(HeadingText20)<{ grayOut: boolean; today: boolean; completed: boolean }>(
    ({ grayOut, today, completed }) => ({
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: grayOut ? colors.gray40 : colors.gray85,
        opacity: grayOut ? 0.7 : 1,
        border: today ? `${pxToRem(2)} solid ${colors.violet55}` : 'none',
        background: completed
            ? `${colors.violet55} url(${checkIcon}) no-repeat center center / 13px`
            : 'inherit',
        fontSize: completed ? 0 : 'inherit',
        height: pxToRem(36),
        width: pxToRem(36),
        borderRadius: '50%',
    }),
);
